import { API_BASE_URL } from './config';
import { extractError, safeJson } from './auth';
import type { LoginMethodResponse, LoginResponse, SsoConfig, SsoStartResponse } from './types/auth';

export class SsoError extends Error {
  public readonly code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'SsoError';
    this.code = code;
  }
}

const DISABLED: SsoConfig = { enabled: false, logoutUrl: null };

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new SsoError(extractError(data, fallback), typeof data.error === 'string' ? data.error : null);
  }
  return data as unknown as T;
}

export async function getSsoConfig(): Promise<SsoConfig> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/sso/config`);
    if (!response.ok) return DISABLED;
    return (await safeJson(response)) as unknown as SsoConfig;
  } catch {
    return DISABLED;
  }
}

export async function getLoginMethod(email: string): Promise<LoginMethodResponse | null> {
  const response = await fetch(`${API_BASE_URL}/auth/login-method?email=${encodeURIComponent(email)}`);
  if (!response.ok) return null;
  return (await safeJson(response)) as unknown as LoginMethodResponse;
}

export function startSso(provider: string): Promise<SsoStartResponse> {
  return postJson('/auth/sso/start', { provider }, 'Single sign-on could not be started');
}

export function completeSso(code: string, state: string): Promise<LoginResponse> {
  return postJson('/auth/sso/callback', { code, state }, 'Single sign-on failed');
}
